import React, { useMemo, useState } from 'react';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { createStyles } from 'utils/createStyle';
import useMediaQueries from 'hooks/useMediaQueries';

interface BrandTitleSectionProps {
    brand?: ECommBrand;
    rebate: number;
    bonusRebate?: number;
    children?: React.ReactNode;
}

interface IFontSizes {
    activeRebate: number;
    oldRebate: number;
    header: number;
}

const useFontSizes = () => {
    const [font, setStyles] = useState<IFontSizes>({
        activeRebate: 29,
        oldRebate: 22,
        header: 42,
    });

    const queries = useMemo(
        () => ({
            '(min-width: 648px)': () => {
                // desktop view
                setStyles({
                    activeRebate: 29,
                    oldRebate: 22,
                    header: 42,
                });
            },
            '(max-width: 647px)': () => {
                // mobile view
                setStyles({
                    activeRebate: 20,
                    oldRebate: 16,
                    header: 28,
                });
            },
        }),
        []
    );
    useMediaQueries(queries);
    return font;
};

interface IStyles {
    header: string;
    bonusRebate: string;
    oldRebate: string;
    normalRebate: string;
}

const useStyles = (): IStyles => {
    const fonts = useFontSizes();
    return createStyles({
        header: ['font-semibold text-brand-dark text-3xl lg:text-5xl', { fontSize: fonts.header }],
        bonusRebate: [
            'font-semibold text-bonus-pink mr-1 sm:mr-2',
            { fontSize: fonts.activeRebate },
        ],
        oldRebate: ['line-through text-grey-2', { fontSize: fonts.oldRebate }],
        normalRebate: ['text-grey-2', { fontSize: fonts.activeRebate }],
    });
};

const formatBrandName = (brandName: string) => {
    return brandName
        .split('/')
        .map((word) => (word.charAt(0) === ' ' ? `${word}/` : ` ${word}/`))
        .join('')
        .slice(0, -1);
};

export const BrandTitleSection = ({
    brand,
    rebate,
    bonusRebate,
    children,
}: BrandTitleSectionProps) => {
    const classes = useStyles();

    const brandName = formatBrandName(brand?.name || '');
    return (
        <div className="flex-1">
            {brand && <h1 className={classes.header}>{brandName}</h1>}
            {bonusRebate ? (
                <RenderBonusRebate classes={classes} oldRebate={rebate} newRebate={bonusRebate} />
            ) : (
                <Rebate classes={classes} rebate={rebate} />
            )}
            {children}
        </div>
    );
};

const RenderBonusRebate = ({
    oldRebate,
    newRebate,
    classes,
}: {
    oldRebate: number;
    newRebate: number;
    classes: IStyles;
}) => {
    return isFinite(oldRebate) ? (
        <p>
            <span className={classes.bonusRebate}>Earn up to {newRebate.toPrecision()}%</span>
            <span className={classes.oldRebate}> was {oldRebate.toPrecision()}%</span>
        </p>
    ) : null;
};

const Rebate = ({ rebate, classes }: { rebate: number; classes: IStyles }) => {
    return isFinite(rebate) ? (
        <p className={classes.normalRebate}>
            Earn up to <span className="text-light-blue-dark">{rebate.toPrecision()}%</span>
        </p>
    ) : null;
};
